import React, { useEffect, useMemo } from 'react';
import {
	NavigationBar,
	ProfileMenu,
	ProductSwitcher,
} from '@barracuda-internal/bds-core-v2';
import { FaviconData } from '@barracuda-internal/bds-core-v2/dist/Logos/Favicons';
import { Home, Apps } from '@barracuda-internal/bds-core-v2/dist/Icons/Core';
import jwtDecode from 'jwt-decode';
import useAuth from 'src/hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProductData } from '@barracuda-internal/bds-core-v2/dist/UnifiedComponents/ProductSwitcher/hooks/useProductData';
import useCustomComponentStyles from './styles/components';

type NavBarContainerProps = {
	mainMenu: any;
	profileMenus: any;
	initials: string;
	env: string;
};

const getResolvedHostname = (env: string | undefined | null) => {
	const normalizedEnv = (env || '').toLowerCase();
	return normalizedEnv === 'prod' || normalizedEnv === 'prodin'
		? 'https://external-api.production.pam-cudasvc.com'
		: 'https://external-api.qa.pam-cudaops.com';
};

const NavBarContainer: React.FC<NavBarContainerProps> = ({
	mainMenu,
	profileMenus,
	initials,
	env,
}) => {
	const { auth } = useAuth();
	const customClasses = useCustomComponentStyles();
	const currentAccount = auth.accountDetails.account;
	const navigate = useNavigate();
	const location = useLocation();
	const path = location.pathname;

	// TODO: Remove after 4 to 5 release cycles (added Aug 2026) —
	// cleans up stale localStorage keys from previous implementation
	useEffect(() => {
		localStorage.removeItem('pamApiHostname');
		localStorage.removeItem('homeButtonShowInNavigation');
	}, []);

	const hostname = useMemo(() => getResolvedHostname(env), [env]);

	const product_data = useMemo(() => {
		try {
			if (!auth || !auth.oidcUser) return null;

			const oidc_access_token_decoded = jwtDecode(
				auth.oidcUser.access_token,
			);

			const bcc_user_id = JSON.parse(
				oidc_access_token_decoded[
					'http://schemas.barracuda.com/datainspector/bcc_user_id'
				],
			);
			const ext_at = oidc_access_token_decoded['ext_at'];
			return { bcc_user_id, ext_at };
		} catch (error) {
			console.error('Error decoding or parsing token:', error);
			return null;
		}
	}, [auth]);

	const productDataParams = useMemo(() => {
		if (
			!product_data?.bcc_user_id ||
			!currentAccount?.company?.tenantId ||
			!product_data?.ext_at
		) {
			return null;
		}
		return {
			userId: product_data.bcc_user_id,
			accountId: currentAccount.company.tenantId,
			apiToken: product_data.ext_at,
			hostname,
		};
	}, [product_data, currentAccount, hostname]);

	const { data: productData, error: productDataError } =
		useProductData(productDataParams);

	// Extracting global navigation options
	const { globalNavigation = [] } = productData || {};

	// Extracting the Barracuda One home button redirect url
	const homeButtonOptions = globalNavigation.find(
		(option) => option.id === 'home-barracuda-one',
	);

	const homeButtonShowInNavigation =
		!productDataError && !!homeButtonOptions?.showInNavigation;

	const renderProductSwitcher = useMemo(() => {
		if (
			productDataError ||
			!productData?.subscriptionGroups ||
			productData.subscriptionGroups.length === 0
		) {
			return (
				<div className={customClasses['ml_10']}>
					No subscription groups found
				</div>
			);
		}

		return (
			<ProductSwitcher
				subscriptionGroups={productData.subscriptionGroups}
			/>
		);
	}, [productDataError, productData, customClasses]);

	return (
		<>
			<NavigationBar
				logo={<FaviconData />}
				options={[
					{
						name: 'Apps',
						icon: <Apps />,
						menu: renderProductSwitcher,
					},
					{
						name: 'Profile',
						initials: initials,
						disabled: false,
						menu: (
							<ProfileMenu
								initials={initials}
								name={auth.user.name}
								email={auth.user.email}
								menuItems={profileMenus}
							/>
						),
					},
				]}
				topOptions={
					homeButtonShowInNavigation && homeButtonOptions?.href
						? [
								{
									name: 'Home',
									icon: <Home />,
									href: homeButtonOptions?.href,
								},
						  ]
						: []
				}
				routes={mainMenu}
				currentPath={path}
				onNavigate={(nav) => {
					navigate(nav);
				}}
			/>
		</>
	);
};

export default NavBarContainer;
